import { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, Boxes, Calendar, CalendarClock, Clock, Package, Plus, User, UserMinus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import Header from '../components/Header';
import { ConfirmDialog } from '../components/ui/alert-dialog-helper';
import { ProductThumbnail } from '../components/ui/aspect-ratio-image';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { DataTable } from '../components/ui/data-table';
import type { ReservationItem, ReservationUser, StockReservation, StockReservationStatus } from '../types/stockReservation';
import { getFirstProductImageUrl } from '../utils/productImage';

type Role = { key: string; name: string; description: string };
type User = { id: string; email: string; first_name: string; last_name: string; roles: Role[] };

const statusVariant: Record<StockReservationStatus, 'default' | 'secondary' | 'warning'> = {
  active: 'default',
  released: 'secondary',
  expired: 'warning',
};

const personName = (u?: ReservationUser | null) => (u ? `${u.first_name} ${u.last_name}` : 'Not set');
const formatDt = (value?: string | null) => (value ? new Date(value).toLocaleString() : 'Not set');

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

type ProductRow = {
  id: string;
  name: string;
  template_code: string;
  quantity: number;
  status: string;
  units: Array<{ id: string; instance_number: string }>;
  images?: Array<{ id: string; url?: string | null; variant?: string; position?: number }>;
};

function groupProducts(items: ReservationItem[]): ProductRow[] {
  const byId = new Map<string, ProductRow>();
  for (const item of items) {
    const inst = item.asset_instance;
    const tpl = inst?.template;
    const id = tpl?.id ?? item.asset_instance_id;
    const unit = inst ? { id: inst.id, instance_number: inst.instance_number } : null;
    const existing = byId.get(id);
    if (existing) {
      existing.quantity += 1;
      if (unit) existing.units.push(unit);
      continue;
    }
    byId.set(id, {
      id,
      name: tpl?.name ?? 'Unknown product',
      template_code: tpl?.template_code ?? '-',
      quantity: 1,
      status: inst?.current_status ?? 'unknown',
      units: unit ? [unit] : [],
      images: tpl?.images,
    });
  }
  return Array.from(byId.values());
}

const productColumns: ColumnDef<ProductRow>[] = [
  {
    accessorKey: 'name',
    header: 'Product',
    cell: ({ row }) => (
      <div className="flex items-center gap-3">
        <ProductThumbnail
          src={getFirstProductImageUrl(row.original.images)}
          alt={row.original.name}
          size="sm"
          fallback={<Package className="h-6 w-6 text-muted-foreground" />}
        />
        <Link to={`/assets/${row.original.id}`} className="font-medium text-gray-900 underline hover:no-underline">
          {row.original.name}
        </Link>
      </div>
    ),
  },
  {
    accessorKey: 'template_code',
    header: 'Product code',
    cell: ({ row }) => (
      <span className="font-mono text-sm text-muted-foreground">{row.original.template_code}</span>
    ),
  },
  {
    id: 'units',
    header: 'Unit codes',
    cell: ({ row }) => (
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        {row.original.units.map((unit) => (
          <Link
            key={unit.id}
            to={`/units/${unit.id}`}
            className="font-mono text-sm underline hover:no-underline"
          >
            {unit.instance_number}
          </Link>
        ))}
      </div>
    ),
  },
  {
    accessorKey: 'quantity',
    header: 'Qty',
    cell: ({ row }) => <span className="font-medium">{row.original.quantity}</span>,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: ({ row }) => (
      <Badge variant="secondary">{row.original.status.replace(/_/g, ' ')}</Badge>
    ),
  },
];

export default function ReservationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [reservation, setReservation] = useState<StockReservation | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releasing, setReleasing] = useState(false);

  useEffect(() => {
    try {
      const saved = localStorage.getItem('user');
      if (saved) setUser(JSON.parse(saved));
    } catch {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token || !id) return;
    setLoading(true);
    setError(null);
    fetch(`/api/stock-reservations/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok || !data.success) throw new Error(data.message || 'Reservation not found');
        setReservation(data.data);
      })
      .catch((err) => setError(err instanceof Error ? err.message : 'Failed to load reservation'))
      .finally(() => setLoading(false));
  }, [id]);

  const release = async () => {
    const token = localStorage.getItem('token');
    if (!token || !id) return;
    setReleasing(true);
    try {
      const r = await fetch(`/api/stock-reservations/${id}/release`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await r.json();
      if (!r.ok || !data.success) throw new Error(data.message || 'Release failed');
      toast.success('Reservation released');
      setReservation(data.data);
      setReleaseOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Release failed');
    } finally {
      setReleasing(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="reservations" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">Loading reservation details...</div>
        </main>
      </div>
    );
  }

  if (error || !reservation) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="reservations" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-12">
            <h2 className="text-xl font-heading text-destructive mb-4">{error || 'Reservation not found'}</h2>
            <Button onClick={() => navigate('/reservations')} variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Reservations
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const items = reservation.items ?? [];
  const products = groupProducts(items);

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="reservations" />
      <main className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/reservations')} className="h-8 w-8 shrink-0">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-3 min-w-0">
            <h1 className="text-2xl font-heading font-bold text-primary truncate">{reservation.project_code}</h1>
            <Badge variant={statusVariant[reservation.status]}>
              {reservation.status.replace('_', ' ').toUpperCase()}
            </Badge>
          </div>
          {reservation.status === 'active' && (
            <div className="ml-auto flex gap-2 shrink-0">
              <Button variant="outline" onClick={() => navigate(`/reservations/new?reservationId=${reservation.id}`)}>
                <Plus className="h-4 w-4 mr-2" />
                Add products
              </Button>
              <Button onClick={() => setReleaseOpen(true)}>Release stock</Button>
            </div>
          )}
        </div>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Reservation Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-start gap-2">
                <User className="h-5 w-5 text-muted-foreground mt-0.5" />
                <Fact label="Reserved by" value={personName(reservation.reserved_by_user)} />
              </div>
              <div className="flex items-start gap-2">
                <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                <Fact label="Duration" value={`${reservation.duration_days} days`} />
              </div>
              <div className="flex items-start gap-2">
                <Boxes className="h-5 w-5 text-muted-foreground mt-0.5" />
                <Fact label="Units" value={String(items.length)} />
              </div>
              <div className="flex items-start gap-2">
                <Calendar className="h-5 w-5 text-muted-foreground mt-0.5" />
                <Fact label="Starts" value={formatDt(reservation.starts_at)} />
              </div>
              <div className="flex items-start gap-2">
                <CalendarClock className="h-5 w-5 text-muted-foreground mt-0.5" />
                <Fact label="Expires" value={formatDt(reservation.expires_at)} />
              </div>
              {reservation.released_at ? (
                <div className="flex items-start gap-2">
                  <UserMinus className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <Fact
                    label="Released"
                    value={`${personName(reservation.released_by_user)} (${formatDt(reservation.released_at)})`}
                  />
                </div>
              ) : null}
            </div>
          </CardContent>
        </Card>

        <div>
          <h2 className="text-lg font-heading font-semibold mb-2">Reserved products ({products.length})</h2>
          <DataTable
            columns={productColumns}
            data={products}
            showColumnToggle={false}
            emptyMessage="No products on this reservation."
          />
        </div>
      </main>
      <ConfirmDialog
        open={releaseOpen}
        onOpenChange={setReleaseOpen}
        title="Release reservation"
        description="These warehouse units will become available again."
        confirmText="Release"
        onConfirm={release}
        loading={releasing}
      />
    </div>
  );
}
